/**
 * TrialBanner — slim strip shown to trial users with live countdown.
 * Renders nothing for non-trial accounts.
 */
import { useEffect, useState } from 'react';
import { Clock, Sparkles } from 'lucide-react';
import { useAuth } from '@/lib/auth';

function formatRemaining(ms) {
  if (ms <= 0) return 'expired';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h >= 1) return `${h}h ${m}m left`;
  return `${m}m left`;
}

export default function TrialBanner() {
  const { user, logout } = useAuth();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!user?.is_trial) return;
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [user?.is_trial]);

  if (!user?.is_trial) return null;

  const trialDays = user.trial_days || 1;
  const trialLabel = `${trialDays}-day`;
  const expiresAt = user.trial_expires_at ? new Date(user.trial_expires_at).getTime() : null;
  if (!expiresAt) return null;
  const remaining = expiresAt - now;
  const expired = remaining <= 0;

  return (
    <div className={`shrink-0 px-4 py-1.5 text-xs flex items-center justify-between gap-3 ${
      expired ? 'bg-rose-50 border-b border-rose-200 text-rose-900'
              : 'bg-[#F26B1F]/10 border-b border-[#F26B1F]/20 text-slate-800'
    }`} data-testid="trial-banner">
      <div className="flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-[#F26B1F]" />
        <span className="font-medium">
          {expired ? `${trialLabel} trial expired` : `You are on a ${trialLabel} trial`}
        </span>
        <span className="text-slate-500">·</span>
        <span className="hidden sm:inline text-slate-600">
          Read-only access to demo data. Admin actions are disabled.
        </span>
      </div>
      <div className="flex items-center gap-3">
        <span className={`flex items-center gap-1.5 font-mono ${expired ? 'text-rose-700' : 'text-[#F26B1F]'}`}>
          <Clock className="w-3 h-3" />
          {formatRemaining(remaining)}
        </span>
        {expired ? (
          <button onClick={logout}
                  className="text-rose-700 underline underline-offset-2 font-medium"
                  data-testid="trial-banner-logout-btn">
            Sign out
          </button>
        ) : (
          <a href="mailto:sales@procurex.io?subject=Upgrade%20my%20ProcureX%20trial"
             className="text-[#F26B1F] hover:underline font-medium"
             data-testid="trial-banner-upgrade-link">
            Upgrade →
          </a>
        )}
      </div>
    </div>
  );
}
